import { CreatedUpdatedBy } from 'modules/operators/interfaces';
import { DeviceValidationSettings } from 'modules/environments/interfaces';

export interface ConfigAplicationProps {
  'plugin-status': boolean;
  'keep-alive': boolean;
}

export interface ConnectionSimulationProps {
  email: string;
  password: string;
}

export interface TestConnectionProps {
  email: string;
  password: string;
}

export interface CreateClientProps {
  name: string;
  environment_uuid: string;
  email_admin: string;
  copy_admin: boolean;
  external_client_code?: string;
  licenses: {
    available_licenses_to_client: number;
    block_same_user_multiple_session: boolean;
    client_license: {
      has_license_limit: boolean;
      license_limit: number;
    };
    license_group: {
      uuid: string;
      name: string;
      license_limit: number;
    };
  };
  active_directory: {
    profile_path: string;
    remote_profile: boolean;
  };
  user_connection: {
    virtualizer?: string;
    connect_port?: number | null;
    service_port?: number | null;
    use_ios_devices: boolean;
  };
  application_options: {
    disable_connection_status: boolean;
    enable_keepalive: boolean;
  };
  user_mfa_is_active?: boolean;
  new_front: boolean | null;
  user_access_without_ad: {
    enabled: boolean | null;
    environment_enabled: boolean;
  };
}

export interface CloneClientProps {
  name: string;
}

export interface FormImportClients {
  csv_file: File | string;
}

export interface FormImportUsers {
  csv_file: File | string;
  set_password?: boolean;
}

export interface FormClientUser {
  first_name: string;
  last_name: string;
  email: string;
  fictitious_email: boolean;
  userAd?: string;
  profile_path?: string;
  use_profile?: boolean;
  ad_password_salt: {
    skip: boolean;
    skip_reason?: string;
    salt: string;
  };
}

export enum CloningStatusEnum {
  requested = 'requested',
  started = 'started',
  finished = 'finished',
}

export interface ClientSSO {
  allow_users_from_group: string;
  client_realm_id: string;
  federation_type: string;
  restrict_access_by_groups: boolean;
  saml_idp_claim_email_address: string;
  saml_idp_claim_given_name: string;
  saml_idp_claim_groups: string;
  saml_idp_claim_name_id: string;
  saml_idp_claim_surname: string;
  saml_idp_entity_id: string;
  saml_idp_public_x509_cert: string;
  saml_idp_single_logout_url: string;
  saml_idp_single_sign_on_url: string;
}

export interface ClientDUO {
  enabled: boolean;
  duo_client_id?: string;
  duo_client_secret?: string;
  host?: string;
  bypass_if_unavailable?: boolean;
}

export interface ClientFields {
  client_uuid: string;
  name: string;
  external_client_code?: string;
  company: {
    uuid: string;
    name: string;
  };
  partner: {
    partner_uuid: string;
    name: string;
    new_front: boolean;
    enforce_env_license_limit: boolean;
    allow_sso_configuration: boolean;
    allow_duo_configuration: boolean;
  };
  environment: {
    environment_uuid: string;
    name: string;
    client_apps: {
      registration_source: string;
      registration_mode: 'simple' | 'advanced';
    };
    licenses: {
      license_limit: number;
      increase_license_limit_each: number;
      control_licenses_by_client: boolean;
      block_same_user_multiple_session: boolean;
      bypass_increase_license_auto_disable: boolean;
      increase_license_reason: string;
    };
    display_warning_enabled: boolean;
    display_warning_end_datetime: Date | null;
    device_validation_settings: DeviceValidationSettings;
  };
  identification: string;
  clone_status: CloningStatusEnum;
  email_admin: string;
  copy_admin: string;
  licenses: {
    available_licenses_to_client: number;
    block_same_user_multiple_session: boolean;
    client_license: {
      has_license_limit: boolean;
      license_limit: number;
    };
    license_limit: number;
    license_group: {
      uuid: string;
      name: string;
      license_limit: number;
    };
  };
  active_directory: {
    profile_path: string;
    remote_profile: boolean;
    security_group: string;
    partner_ldap_type: string;
    ad_password_salt_enabled: boolean;
  };
  user_connection?: {
    virtualizer: string;
    connect_port: number | null;
    service_port: number | null;
    use_ios_devices: boolean;
    connection_type: {
      enabled: boolean;
      type: 'public' | 'private' | null;
      environment_allow_configuration: boolean;
    };
  };
  application_options: {
    disable_connection_status: boolean;
    enable_keepalive: boolean;
  };
  metrics: {
    sessions: {
      active: number;
    };
  };
  user_mfa_is_active: boolean;
  sso?: ClientSSO;
  sso_enabled?: boolean;
  user_last_access: null | Date;
  in_maintenance: boolean;
  new_front: boolean | null;
  duo?: ClientDUO;
  observations: string;
  user_access_without_ad: {
    enabled: boolean | null;
    environment_enabled: boolean;
  };
}

export interface AllClientsFields {
  partner_name: string;
  partner_uuid: string;
  company_name: string;
  company_uuid: string;
  environment_name: string;
  environment_uuid: string;
  client_name: string;
  client_uuid: string;
  num_users: number;
  user_last_access: null | Date;
  in_maintenance: boolean;
}

export interface HistorySessionFields {
  user: {
    full_name: string;
    email: string;
  };
  is_admin: boolean;
  creation_date: string;
  termination_date: string;
  my_ip: string;
  ec2_public_ip: string;
  session_id: string;
  termination_reason: string;
}

export type ListClientDataProps = PaginatedResponse<ClientFields>;

export type ListAllClientsDataProps = PaginatedResponse<AllClientsFields>;

export interface HistorySessionProps
  extends PaginatedResponse<HistorySessionFields> {
  loading: boolean;
}

export interface ClientSessionsFields {
  environment_uuid: string;
  session_id: string;
  instance_id: string;
  instance_name: string;
  login_at: string;
  user: {
    email: string;
    first_name: string;
    last_name: string;
  };
  status: 'active' | 'started' | 'removing' | 'close';
}

export interface ClientApplicationsFields {
  app_uuid: string;
  app_name: string;
  app_code: string;
  name: string;
  state: 'active' | 'inactive';
}

export interface ClientWebApplicationsFields {
  app_uuid: string;
  name: string;
  url: string;
  enabled: boolean;
}

export type ListClientDataSessionProps = PaginatedResponse<
  ClientSessionsFields
>;

export type ListClientDataApplicationProps = PaginatedResponse<
  ClientApplicationsFields
>;

export interface ListAvailableApplicationsProps
  extends PaginatedResponse<{ app_code: string; app_name: string }> {
  loading: boolean;
}

export interface ClientUsers {
  user_uuid: string;
  first_name: string;
  profile_path: string;
  use_profile: boolean;
  last_name: string;
  email: string;
  active: boolean;
  fictitious_email: boolean;
  is_integration_user: boolean;
  mfa_active: boolean;
  mfa_configured: boolean;
  has_safe_device_enabled: boolean;
  partner: {
    active_directory: {
      username: string;
      password_change: {
        success: boolean;
        last_change: string;
      };
    };
  };
  ad_password_salt: {
    skip: boolean;
    skip_reason?: string;
    salt: string;
  };
  created_by_operator: CreatedUpdatedBy | null;
  created_at: string;
  updated_by_operator: CreatedUpdatedBy | null;
  updated_at: string;
  user_agent_info: UserAgentInfo;
  user_last_access: null | Date;
  reset_password_at_login: boolean;
  device_validation_bypass: boolean;
  device_validation_bypass_reason: string;
  device_validation_bypass_at: string;
  device_validation_bypass_by: string;
}

interface UserAgentInfo {
  browser_family: string | null;
  os_family: string | null;
}

export interface ClientsMaintenanceDisplayWarningProps {
  active: boolean;
  start_date: string;
  end_date: string;
  message: string;
  display_to_logged_users: boolean;
}

export type ListClientUsersDataProps = PaginatedResponse<ClientUsers>;

export interface AllowedIpProps {
  id: number;
  ip_address: string;
}

export type ListAllowedIpsDataProps = PaginatedResponse<AllowedIpProps>;

export interface BlockedPeriodsProps {
  id?: number;
  weekdays: number[];
  start_restriction_at: string;
  stop_restriction_at: string;
  timezone: string;
}

export enum WeekDaysValuesEnum {
  MONDAY = 0,
  TUESDAY = 1,
  WEDNESDAY = 2,
  THURSDAY = 3,
  FRIDAY = 4,
  SATURDAY = 5,
  SUNDAY = 6,
}

export type ListBlockedPeriodsDataProps = PaginatedResponse<
  BlockedPeriodsProps
>;

export interface SettingsAccessControlProps {
  control_by_schedule: boolean;
  control_by_ip: boolean;
}

export interface AccessControlProps {
  settings: SettingsAccessControlProps;
  allowedIps: ListAllowedIpsDataProps;
  blockedPeriods: ListBlockedPeriodsDataProps;
  loading: boolean;
}

export interface TestConnectionResponse {
  virtualizer:
    | 'connect'
    | 'connect_direct'
    | 'rdp_direct'
    | 'rdp'
    | 'rdp_app'
    | 'tsplus'
    | 'tsplus_remote_app'
    | 'tsplus_all';
  url: string;
  chrome_url: string;
  parameters: any;
  rdp_file_contents: string;
}

export interface AuditLog {
  action_time: string;
  operator_email: string;
  object: string;
  action_flag: number;
  action_name: string;
}

export interface FormUser {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
}

export interface BlockMultipleSessionReportProps {
  activate: number;
  deactivate: number;
}
export interface ConnectionTypeReportProps {
  private: number;
  public: number;
}
export interface MFAReportProps {
  activate: number;
  deactivate: number;
}
export interface UseProfileReportProps {
  activate: number;
  deactivate: number;
}
export interface VirtualizerReportProps {
  connect: number;
  connect_direct: number;
  rdp: number;
  rdp_direct: number;
  rdp_app: number;
  tsplus: number;
  tsplus_remote_app: number;
  tsplus_all: number;
}

export interface ActivateReportProps {
  activate: number;
  deactivate: number;
}
export interface SaltReportProps {
  activate: number;
  deactivate: number;
}

export interface DigitalCertificatesProps {
  path: string;
  uuid: string;
}

export type ListDigitalCertificatesDataProps = PaginatedResponse<
  DigitalCertificatesProps
>;

export interface ClientsState {
  readonly clientList: ListClientDataProps;
  readonly clientsList: ListAllClientsDataProps;
  readonly clientSelected: ClientFields | null;
  readonly clientUsers: ListClientUsersDataProps;
  readonly isLoading: boolean;
  readonly isLoadingUsers: boolean;
  readonly isConnecting: boolean;
  readonly listSessionClients: ListClientDataSessionProps;
  readonly listApplications: ListClientDataApplicationProps;
  readonly listAvailableApplications: ListAvailableApplicationsProps;
  readonly clientsMaintenanceDisplayWarning: ClientsMaintenanceDisplayWarningProps;
  readonly testConnection: TestConnectionResponse | null;
  readonly accessControl: AccessControlProps;
  readonly historySessions: HistorySessionProps;
  readonly urlConnectionRemoteApp: string;
  readonly urlChromeExtension: string;
  readonly blockMultipleSessionReport: BlockMultipleSessionReportProps;
  readonly isLoadingBlockMultipleSessionReport: boolean;
  readonly connectionTypeReport: ConnectionTypeReportProps;
  readonly isLoadingConnectionTypeReport: boolean;
  readonly MFAReport: MFAReportProps;
  readonly isLoadingMFAReport: boolean;
  readonly useProfileReport: UseProfileReportProps;
  readonly isLoadingUseProfileReport: boolean;
  readonly virtualizerReport: VirtualizerReportProps;
  readonly isLoadingVirtualizerReport: boolean;
  readonly activateReport: ActivateReportProps;
  readonly isLoadingActivateReport: boolean;
  readonly saltReport: SaltReportProps;
  readonly isLoadingSaltReport: boolean;
  readonly digitalCertificates: ListDigitalCertificatesDataProps;
  readonly isLoadingCertificates: boolean;
}
